
import { useState, useEffect } from "react";
import { Canvas } from "fabric";
import { Button } from "@/components/ui/button";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { MeasurementService } from "@/services/measurement-service";
import { debouncedRender } from "@/utils/canvas-rendering-utils";
import { calculatePixelsPerMm } from "@/utils/measurement-utils";

interface ZoomControlsProps {
  canvas: Canvas | null;
  onZoomChange?: (zoom: number) => void;
}

export const ZoomControls = ({ canvas, onZoomChange }: ZoomControlsProps) => {
  const [zoom, setZoom] = useState(100);
  const minZoom = 25;
  const maxZoom = 400;

  useEffect(() => {
    if (!canvas) return;
    if (!canvas.wrapperEl) return;
    
    console.log('Updating canvas zoom:', zoom);
    const zoomValue = zoom / 100;
    const center = canvas.getCenter();
    
    try {
      canvas.setViewportTransform([
        zoomValue,
        0,
        0,
        zoomValue,
        canvas.width! / 2 - center.left * zoomValue,
        canvas.height! / 2 - center.top * zoomValue
      ]);
      
      // Force recalculation of measurement scale with current zoom
      // This ensures the calibration is updated with the zoom
      calculatePixelsPerMm(null, canvas as any);
      
      // Update all measurement labels with the new zoom value using the measurement service
      MeasurementService.updateAllMeasurements(canvas, zoomValue);
      
      debouncedRender(canvas);
      onZoomChange?.(zoom);
    } catch (error) {
      console.error("Error updating canvas zoom:", error);
    }
  }, [zoom, canvas, onZoomChange]);

  const handleZoomIn = () => {
    if (zoom < maxZoom) {
      const newZoom = Math.min(zoom + 25, maxZoom);
      setZoom(newZoom);
    }
  };

  const handleZoomOut = () => {
    if (zoom > minZoom) {
      const newZoom = Math.max(zoom - 25, minZoom);
      setZoom(newZoom);
    }
  };

  return (
    <div className="relative group scale-80 transform-origin-bottom-left">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/20 to-purple-400/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="bg-white/90 backdrop-blur-md rounded-full shadow-md border border-indigo-100/70 flex overflow-hidden transition-all duration-300 group-hover:shadow-lg relative">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomOut}
            disabled={zoom <= minZoom}
            className={cn(
              "h-6 w-6 rounded-none border-r border-indigo-100/50",
              "hover:bg-indigo-50/80 active:bg-indigo-100/60",
              "text-indigo-500 transition-all duration-300",
              zoom <= minZoom && "opacity-40 cursor-not-allowed"
            )}
            title="Zoom out"
          >
            <Minus className="h-2.5 w-2.5 transition-transform duration-200 group-hover:scale-110" />
          </Button>

          <div className="px-1.5 flex items-center justify-center">
            <span className="text-[10px] font-medium text-indigo-500 tracking-tight min-w-[24px] text-center">
              {zoom}%
            </span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomIn}
            disabled={zoom >= maxZoom}
            className={cn(
              "h-6 w-6 rounded-none border-l border-indigo-100/50",
              "hover:bg-indigo-50/80 active:bg-indigo-100/60",
              "text-indigo-500 transition-all duration-300",
              zoom >= maxZoom && "opacity-40 cursor-not-allowed"
            )}
            title="Zoom in"
          >
            <Plus className="h-2.5 w-2.5 transition-transform duration-200 group-hover:scale-110" />
          </Button>
        </div>
      </div>
    </div>
  );
};
